import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { SnackbarCloseReason, Theme } from '@mui/material';
import { Alert, Box, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import type { FC } from 'react';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import ErrorDetailsModal from 'components/modals/ErrorDetailsModal';
import type { ApiErrorDataI } from 'declarations/interfaces/errors';
import type { SnackbarSeverityT } from 'reduxStore/features/notifications/notificationsSlice';

export const DEFAULT_NOTIFICATION_SUCCESS_DURATION = 3000;

export const DEFAULT_NOTIFICATION_ERROR_DURATION = 60 * 60 * 1000;

interface SnackbarNotificationProps {
  errorDetails?: ApiErrorDataI | null;
  errorSource?: string;
  open: boolean;
  onClose: (
    event: React.SyntheticEvent<Element, Event> | Event,
    reason?: SnackbarCloseReason
  ) => void;
  message: string;
  subMessage?: string;
  severity: SnackbarSeverityT;
  backgroundColor?: string;
}

const SnackbarNotification: FC<SnackbarNotificationProps> = ({
  errorDetails,
  errorSource,
  open,
  onClose,
  message,
  subMessage,
  severity,
  backgroundColor,
}): JSX.Element => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const showDetails = severity === 'error';

  const detailsPayload: ApiErrorDataI =
    errorDetails ??
    ({
      title: message || 'Error',
      detail: 'No additional error details are available.',
      status: 0,
    } as ApiErrorDataI);

  const getSnackbarStyles = (): Record<string, unknown> => ({
    backgroundColor: backgroundColor
      ? backgroundColor
      : (theme: Theme): string => {
          switch (severity) {
            case 'success':
              return theme.palette.success.main;
            case 'warning':
              return theme.palette.warning.main;
            case 'error':
            default:
              return theme.palette.error.main;
          }
        },
    color: (theme: Theme): string => {
      if (backgroundColor) {
        return theme.palette.primary.contrastText;
      }
      return severity === 'warning'
        ? theme.palette.warning.contrastText
        : theme.palette.common.white;
    },
  });

  const getSnackbarIcon = (): React.ReactNode => {
    const getIconColor = (theme: Theme): string => {
      if (backgroundColor) {
        return theme.palette.primary.contrastText;
      }
      return severity === 'warning'
        ? theme.palette.warning.contrastText
        : theme.palette.common.white;
    };
    if (severity === 'success') {
      return <CheckIcon sx={{ fontSize: 20, color: getIconColor, marginRight: 1 }} />;
    }
    return <ErrorOutlineIcon sx={{ fontSize: 20, color: getIconColor, marginRight: 1 }} />;
  };

  const snackbar = (
    <>
      {showDetails && (
        <ErrorDetailsModal
          errorDetails={detailsPayload}
          errorSource={errorSource}
          open={detailsOpen}
          onClose={(): void => setDetailsOpen(false)}
        />
      )}
      <Snackbar
        open={open}
        autoHideDuration={
          severity === 'error'
            ? DEFAULT_NOTIFICATION_ERROR_DURATION
            : DEFAULT_NOTIFICATION_SUCCESS_DURATION
        }
        onClose={(event, reason): void => {
          if (reason === 'clickaway') {
            return;
          }
          onClose(event, reason);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          userSelect: 'text',
          WebkitUserSelect: 'text',
          MozUserSelect: 'text',
          msUserSelect: 'text',
          ...(severity === 'error' && {
            zIndex: (theme: Theme): number => theme.zIndex.snackbar + 100,
          }),
          '& .MuiSnackbarContent-root': {
            userSelect: 'text',
            WebkitUserSelect: 'text',
            MozUserSelect: 'text',
            msUserSelect: 'text',
          },
        }}
      >
        <Alert
          action={
            <Box alignItems="center" display="flex" gap={1}>
              {severity === 'error' && (
                <Tooltip arrow title="Open Error Details Modal">
                  <IconButton
                    aria-label="open error details modal"
                    color="inherit"
                    onClick={(): void => setDetailsOpen(true)}
                    size="small"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" aria-label="close" color="inherit" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          severity={severity}
          sx={{
            width: '100%',
            ...getSnackbarStyles(),
            userSelect: 'text',
            WebkitUserSelect: 'text',
            MozUserSelect: 'text',
            msUserSelect: 'text',
            cursor: 'text',
            '& .MuiAlert-message': {
              userSelect: 'text',
              WebkitUserSelect: 'text',
              MozUserSelect: 'text',
              msUserSelect: 'text',
              cursor: 'text',
              pointerEvents: 'auto',
            },
          }}
          icon={getSnackbarIcon()}
        >
          <Typography
            sx={{
              userSelect: 'text',
              WebkitUserSelect: 'text',
              MozUserSelect: 'text',
              msUserSelect: 'text',
              cursor: 'text',
              pointerEvents: 'auto',
            }}
          >
            {message}
          </Typography>
          {subMessage && severity !== 'error' && (
            <Typography
              fontSize={14}
              sx={{
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                msUserSelect: 'text',
                cursor: 'text',
                pointerEvents: 'auto',
              }}
            >
              {subMessage}
            </Typography>
          )}
        </Alert>
      </Snackbar>
    </>
  );

  const snackbarRoot = document.getElementById('snackbar-root');
  if (!snackbarRoot) {
    return snackbar;
  }

  return ReactDOM.createPortal(snackbar, snackbarRoot) as JSX.Element;
};

export default SnackbarNotification;
